/**
 * Componente: Padrón de Contribuyentes
 * Listado + búsqueda + alta/edición de contribuyentes que tramitan
 * la Licencia Única Nacional de Conducir.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import PadronFormModal from './PadronFormModal';
import PadronPreviewModal from './PadronPreviewModal';
import ConfiguracionFotoModal from './ConfiguracionFotoModal';
import ConfirmarImpresionModal from './ConfirmarImpresionModal';
import ReportarFalloImpresionModal from './ReportarFalloImpresionModal';
import ConfirmarRenovacionModal from './ConfirmarRenovacionModal';
import ConfirmarCorteClasesModal from './ConfirmarCorteClasesModal';
import SolicitarReimpresionModal from './SolicitarReimpresionModal';
import ZonaImprimible from './ZonaImprimible';
import { esperarImagenes, precargarImagenesFondo } from '../utils/generarPDFLicencia';
import { useClasesMunicipio, cargarClasesMunicipio, tituloClase } from '../utils/clasesLicencia';
import { esperarTipografias, clasesSeCortanEnDiseno } from '../utils/formatoClases';
import { formatearFecha } from '../utils/fecha';
import {
  STROKE_DEGRADE,
  IconBuscar,
  IconConfiguracion,
  IconAgregar,
  IconExito,
  IconAdvertencia,
  IconEditar,
  IconVistaPrevia,
  IconImprimir,
  IconRenovar,
  IconAccionRapida,
} from './icons';
import '../styles/padron.css';

const API_URL = process.env.REACT_APP_API_URL;

// Props fijas para los íconos de los botones de acción de la tabla: chico y
// con el trazo en degradé de marca (modo "bare", sin insignia circular —
// la fila tiene demasiados botones para un badge por cada uno).
const ICONO_BOTON = { size: 14, stroke: STROKE_DEGRADE };

const ESTADOS_LICENCIA = {
  vigente: { label: 'Vigente', clase: 'vigente' },
  proxima_a_vencer: { label: 'Próxima a vencer', clase: 'proxima' },
  vencida_en_gracia: { label: 'En período de gracia', clase: 'gracia' },
  vencida_fuera_de_gracia: { label: 'Vencida', clase: 'vencida' },
};

// Aclaración chica en el badge de "Estado de licencia" cuando el ciclo de
// impresión sigue abierto (estado_impresion !== 'impresa') — ayuda a leer
// de un vistazo por qué esa licencia todavía no se imprimió. Prioridad
// fija: solicitud pendiente > nunca tuvo licencia > origen del reintento.
// Si no matchea nada, no hay sufijo (badge queda igual que hoy).
const sufijoEstadoImpresion = (c) => {
  if (c.estado_impresion === 'impresa') return null;
  if (c.tiene_solicitud_pendiente) return 'Esperando autorización';
  if (!c.fecha_impresion) return 'Alta sin emitir';
  if (c.origen_reintento === 'renovacion') return 'Renovación sin imprimir';
  if (c.origen_reintento === 'reimpresion') return 'Reimpresión sin imprimir';
  if (c.origen_reintento === 'reactivacion') return 'Reactivación sin imprimir';
  return null;
};

const Padron = ({ onVolver, esAdmin }) => {
  // Descripciones de las clases del municipio (tooltip de cada etiqueta del listado).
  const { catalogo: catalogoClases } = useClasesMunicipio();
  const [contribuyentes, setContribuyentes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [contribuyenteEditandoId, setContribuyenteEditandoId] = useState(null);
  // Reutiliza el mismo modal/estado de Editar (modalAbierto +
  // contribuyenteEditandoId) — este booleano solo cambia qué modo le pasa
  // a PadronFormModal. Se abre desde "Solicitar alta" (licencia vencida
  // fuera de gracia) en vez de desde el botón "Editar" suelto.
  const [modoSolicitudActivo, setModoSolicitudActivo] = useState(false);
  // Igual patrón que modoSolicitudActivo, pero para cuando el admin resuelve
  // "Solicitar alta" él mismo (reactivar-directo) en vez de mandarle el
  // pedido a otro admin. contribuyenteReactivandoAdmin guarda el objeto
  // completo (no solo el id, a diferencia de contribuyenteEditandoId) porque
  // hace falta íntegro para encadenar a imprimir(c) cuando el modal termina
  // — mismo motivo por el que pedirRenovar(c) guarda el objeto completo.
  const [modoAdminDirectoActivo, setModoAdminDirectoActivo] = useState(false);
  const [contribuyenteReactivandoAdmin, setContribuyenteReactivandoAdmin] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [renovandoId, setRenovandoId] = useState(null);
  // Contribuyente para el que se está por confirmar la renovación (o null)
  // — reemplaza al window.confirm() que había acá, mismo patrón de
  // pedirX/confirmarX que ya usa Auditoria.jsx.
  const [confirmandoRenovacion, setConfirmandoRenovacion] = useState(null);
  // Contribuyente para el que se está por solicitar una reimpresión (o
  // null) — igual patrón que confirmandoRenovacion, pero independiente
  // del candado de impresión: se abre desde una licencia 'impresa' y
  // 'vigente', sin ningún intento de impresión en curso.
  const [solicitandoReimpresion, setSolicitandoReimpresion] = useState(null);
  const [procesandoSolicitudReimpresion, setProcesandoSolicitudReimpresion] = useState(false);
  const [modalConfigFotoAbierto, setModalConfigFotoAbierto] = useState(false);
  // Flujo de impresión real (Fase 3c paso 2b):
  // imprimiendoId -> generando la licencia (candado de toda la tabla, ver botón)
  // imprimiendoDirecto -> { contribuyente, diseno, licenciaId, nombreCompleto }
  //   monta ZonaImprimible con estos datos reales y dispara window.print()
  //   (ver el useEffect de más abajo)
  // confirmandoImpresion -> { licenciaId, nombreCompleto } esperando Sí/No
  // reportandoFallo -> { licenciaId, nombreCompleto } pidiendo motivo (vino del "No")
  const [imprimiendoId, setImprimiendoId] = useState(null);
  const [imprimiendoDirecto, setImprimiendoDirecto] = useState(null);
  const zonaImprimibleRef = useRef(null);
  const [confirmandoImpresion, setConfirmandoImpresion] = useState(null);
  const [reportandoFallo, setReportandoFallo] = useState(null);
  const [procesandoAccionImpresion, setProcesandoAccionImpresion] = useState(false);
  // Contribuyente cuyo carnet saldría con las clases cortadas ("…"): espera la confirmación antes de imprimir.
  const [avisoCorteImpresion, setAvisoCorteImpresion] = useState(null);

  const cargarContribuyentes = useCallback(async (q) => {
    try {
      setCargando(true);
      setError('');
      const token = sessionStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/contribuyentes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: q ? { q } : {},
      });
      setContribuyentes(data.contribuyentes || []);
    } catch (err) {
      console.error('Error al cargar contribuyentes:', err);
      setError(err.response?.data?.mensaje || 'Error al cargar el padrón');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      cargarContribuyentes(busqueda);
    }, 350);
    return () => clearTimeout(timer);
  }, [busqueda, cargarContribuyentes]);

  const abrirNuevo = () => {
    setContribuyenteEditandoId(null);
    setModalAbierto(true);
  };

  const abrirEditar = (id) => {
    setContribuyenteEditandoId(id);
    setModalAbierto(true);
  };

  const abrirSolicitudAlta = (id) => {
    setContribuyenteEditandoId(id);
    setModoSolicitudActivo(true);
    setModalAbierto(true);
  };

  const abrirReactivacionAdmin = (c) => {
    setContribuyenteEditandoId(c.id);
    setContribuyenteReactivandoAdmin(c);
    setModoAdminDirectoActivo(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setContribuyenteEditandoId(null);
    setModoSolicitudActivo(false);
    setModoAdminDirectoActivo(false);
    setContribuyenteReactivandoAdmin(null);
  };

  const alGuardar = () => {
    cerrarModal();
    cargarContribuyentes(busqueda);
  };

  // Mismo patrón que confirmarRenovacion(): cerrar, refrescar la lista, y
  // encadenar directo a imprimir(c) sin esperar un segundo click. Captura
  // el contribuyente en una variable local antes de cerrarModal() (que
  // limpia contribuyenteReactivandoAdmin) para no perderlo.
  const alGuardarReactivacionAdmin = async () => {
    const c = contribuyenteReactivandoAdmin;
    cerrarModal();
    await cargarContribuyentes(busqueda);
    if (c) await imprimir(c);
  };

  // Paso a) generar/reutilizar la licencia + b) montar ZonaImprimible con
  // los datos reales (dispara el useEffect de abajo, que espera las
  // imágenes y recién ahí llama a window.print()).
  const imprimir = async (c, { corteConfirmado = false } = {}) => {
    try {
      setImprimiendoId(c.id);
      setError('');
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Antes de generar nada: si el diseño activo imprime las clases con código + descripción y, para ESTA
      // persona, el texto no entra ni a la letra mínima (se cortaría con "…"), se pide confirmación. Cancelar no
      // deja ningún rastro. Si esta comprobación falla por cualquier motivo, se imprime como siempre.
      if (!corteConfirmado) {
        try {
          const [{ data: act }, catalogo] = await Promise.all([
            axios.get(`${API_URL}/municipios/disenos/activo`, { headers }),
            cargarClasesMunicipio(),
          ]);
          await esperarTipografias();
          const d = act.diseno;
          if (d && clasesSeCortanEnDiseno(d, c.clases, catalogo, d.tipo_impresion === 'doble_cara' ? 'ambas' : 'frente')) {
            setImprimiendoId(null);
            setAvisoCorteImpresion(c);
            return;
          }
        } catch (errAviso) {
          console.error('No se pudo comprobar si las clases entran en el carnet:', errAviso);
        }
      }
      const { data } = await axios.post(`${API_URL}/contribuyentes/${c.id}/licencia/generar`, {}, { headers });

      setImprimiendoDirecto({
        contribuyente: data.contribuyente,
        diseno: data.diseno,
        licenciaId: data.licencia_id,
        nombreCompleto: `${c.apellido}, ${c.nombre}`,
      });
    } catch (err) {
      console.error('Error al generar la licencia:', err);
      setError(err.response?.data?.mensaje || 'Error al generar la licencia');
      setImprimiendoId(null);
    }
  };

  // c) espera a que las imágenes de la zona imprimible (foto, logo) ya
  // hayan cargado + d) window.print() + escucha afterprint UNA sola vez:
  // recién ahí se limpia imprimiendoDirecto y se dispara e) el modal
  // "¿Se imprimió correctamente?" — ni antes, ni con demora. imprimiendoId
  // (el candado de toda la tabla) NO se toca acá a propósito: sigue activo
  // hasta que se responde Sí/No, se libera recién en confirmarImpresionSi()
  // o en enviarReporteFallo().
  useEffect(() => {
    if (!imprimiendoDirecto) return undefined;

    let cancelado = false;

    const dispararImpresionDirecta = async () => {
      try {
        // Las imágenes <img> (foto, logo) vía esperarImagenes; las imágenes
        // de fondo del diseño (fondo_imagen_*_url, aplicadas como CSS
        // background-image, no como <img>) vía precargarImagenesFondo — si
        // no, el diálogo de impresión abría a veces sin el fondo hasta
        // cerrar/reabrir. Dorso solo si el diseño es doble cara.
        const d = imprimiendoDirecto.diseno;
        const urlsFondo = [
          d.fondo_imagen_frente_url,
          d.tipo_impresion === 'doble_cara' ? d.fondo_imagen_dorso_url : null,
        ].filter(Boolean);
        await Promise.all([
          zonaImprimibleRef.current ? esperarImagenes(zonaImprimibleRef.current) : Promise.resolve(),
          precargarImagenesFondo(urlsFondo),
          // La lista de clases (descripciones) y la tipografía tienen que estar listas: el campo "clases" en
          // formato código + descripción se calcula con ellas, y tiene que salir igual que en la vista previa.
          cargarClasesMunicipio(),
          esperarTipografias(),
        ]);
        // Dos cuadros de animación para que el carnet ya dibujado en la zona imprimible incorpore esos datos.
        await new Promise((resolver) => requestAnimationFrame(() => requestAnimationFrame(resolver)));
        if (cancelado) return;

        const alTerminarImpresion = () => {
          setImprimiendoDirecto(null);
          setConfirmandoImpresion({
            licenciaId: imprimiendoDirecto.licenciaId,
            nombreCompleto: imprimiendoDirecto.nombreCompleto,
          });
        };
        window.addEventListener('afterprint', alTerminarImpresion, { once: true });

        window.print();
      } catch (err) {
        console.error('Error al disparar la impresión:', err);
        setError('Error al preparar la impresión');
        setImprimiendoId(null);
        setImprimiendoDirecto(null);
      }
    };

    dispararImpresionDirecta();

    return () => {
      cancelado = true;
    };
  }, [imprimiendoDirecto]);

  // f) Sí -> confirmar-impresion y refrescar para que el botón cambie solo.
  const confirmarImpresionSi = async () => {
    try {
      setProcesandoAccionImpresion(true);
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_URL}/licencias/${confirmandoImpresion.licenciaId}/confirmar-impresion`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfirmandoImpresion(null);
      await cargarContribuyentes(busqueda);
    } catch (err) {
      console.error('Error al confirmar la impresión:', err);
      setError(err.response?.data?.mensaje || 'Error al confirmar la impresión');
      setConfirmandoImpresion(null);
    } finally {
      setProcesandoAccionImpresion(false);
      // Acá se libera el candado de "impresión en curso" — recién termina
      // el ciclo completo, ya sea que confirmar-impresion haya salido bien
      // o mal (no tiene sentido dejar la tabla entera trabada por un error
      // puntual de red al confirmar).
      setImprimiendoId(null);
    }
  };

  // No -> pasa al modal de motivo, sin llamar todavía a ningún endpoint.
  const confirmarImpresionNo = () => {
    setReportandoFallo(confirmandoImpresion);
    setConfirmandoImpresion(null);
  };

  // g) Envía el motivo. Un técnico conocido deja reintentar solo; "otro"
  // dispara el pedido de autorización — en ese caso sí hace falta
  // refrescar la lista para que el botón pase a "Esperando autorización".
  const enviarReporteFallo = async (motivoCategoria, motivoDetalle) => {
    try {
      setProcesandoAccionImpresion(true);
      const token = sessionStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/licencias/${reportandoFallo.licenciaId}/reportar-fallo`,
        { motivo_categoria: motivoCategoria, motivo_detalle: motivoDetalle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportandoFallo(null);
      if (data.puedeReintentar) {
        setMensaje('Podés reintentar');
      } else {
        setMensaje(data.mensaje || 'Pedido de autorización enviado al administrador');
        await cargarContribuyentes(busqueda);
      }
      setTimeout(() => setMensaje(''), 6000);
    } catch (err) {
      console.error('Error al reportar el fallo de impresión:', err);
      setError(err.response?.data?.mensaje || 'Error al reportar el fallo de impresión');
      setReportandoFallo(null);
    } finally {
      setProcesandoAccionImpresion(false);
      // Mismo candado que en confirmarImpresionSi(): recién se libera acá,
      // haya salido reintentable o no (o incluso si el request en sí falló).
      setImprimiendoId(null);
    }
  };

  // Cerrar este modal sin mandar ningún motivo (botón "Cancelar" o la X) es
  // un caso que no estaba explícitamente contemplado en el candado — sin
  // esto, la tabla entera quedaría trabada para siempre sin ninguna forma
  // de destrabarla. Se libera acá también, por las mismas razones.
  const cancelarReporteFallo = () => {
    setReportandoFallo(null);
    setImprimiendoId(null);
  };

  const abrirVistaPrevia = (id) => {
    setPreviewId(id);
  };

  const cerrarVistaPrevia = () => {
    setPreviewId(null);
  };

  const pedirRenovar = (c) => setConfirmandoRenovacion(c);
  const cancelarRenovacion = () => setConfirmandoRenovacion(null);

  const confirmarRenovacion = async () => {
    if (!confirmandoRenovacion) return;
    const c = confirmandoRenovacion;

    try {
      setRenovandoId(c.id);
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_URL}/contribuyentes/${c.id}/renovar`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfirmandoRenovacion(null);
      await cargarContribuyentes(busqueda);
      await imprimir(c);
    } catch (err) {
      console.error('Error al renovar:', err);
      setError(err.response?.data?.mensaje || 'Error al renovar la licencia');
      setConfirmandoRenovacion(null);
    } finally {
      setRenovandoId(null);
    }
  };

  const pedirReimpresion = (c) => setSolicitandoReimpresion(c);
  const cancelarSolicitudReimpresion = () => setSolicitandoReimpresion(null);

  const confirmarSolicitudReimpresion = async (motivoCategoria, motivoDetalle) => {
    if (!solicitandoReimpresion) return;
    const c = solicitandoReimpresion;

    try {
      setProcesandoSolicitudReimpresion(true);
      const token = sessionStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/licencias/${c.licencia_id}/solicitar-reimpresion`,
        { motivo_categoria: motivoCategoria, motivo_detalle: motivoDetalle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSolicitandoReimpresion(null);
      setMensaje(data.mensaje || 'Pedido de reimpresión enviado al administrador');
      setTimeout(() => setMensaje(''), 6000);
      await cargarContribuyentes(busqueda);
    } catch (err) {
      console.error('Error al solicitar la reimpresión:', err);
      setError(err.response?.data?.mensaje || 'Error al solicitar la reimpresión');
      setSolicitandoReimpresion(null);
    } finally {
      setProcesandoSolicitudReimpresion(false);
    }
  };

  return (
    <section className="padron-section">
      {onVolver && (
        <button type="button" className="btn-volver padron-btn-volver" onClick={onVolver}>
          <ArrowLeft size={16} /> Volver
        </button>
      )}
      <div className="padron-toolbar">
        <div className="padron-search-wrapper">
          <span className="padron-search-icon">
            <IconBuscar size={16} stroke={STROKE_DEGRADE} />
          </span>
          <input
            type="text"
            className="padron-search-input"
            placeholder="Buscar por apellido, nombre o documento..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        {esAdmin && (
          <button
            type="button"
            className="btn-accion btn-editar"
            onClick={() => setModalConfigFotoAbierto(true)}
            title="Elegir cómo se carga la foto del contribuyente"
          >
            <IconConfiguracion {...ICONO_BOTON} /> Método de foto
          </button>
        )}
        <button className="btn-nuevo-contribuyente" onClick={abrirNuevo}>
          <IconAgregar size={16} /> Nuevo contribuyente
        </button>
      </div>

      {mensaje && (
        <div className="alert alert-success">
          <IconExito size={18} />
          <p>{mensaje}</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <IconAdvertencia size={18} />
          <p>{error}</p>
        </div>
      )}

      {cargando ? (
        <div className="padron-loading">
          <div className="loading-spinner"></div>
          <p>Cargando padrón...</p>
        </div>
      ) : contribuyentes.length === 0 ? (
        <div className="padron-empty">
          <p>No se encontraron contribuyentes.</p>
        </div>
      ) : (
        <div className="padron-table-wrapper">
          <table className="padron-table">
            <thead>
              <tr>
                <th>Apellido</th>
                <th>Nombre</th>
                <th>Documento</th>
                <th>Clases</th>
                <th>Vencimiento</th>
                <th>Estado de licencia</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {contribuyentes.map((c) => (
                <tr key={c.id}>
                  <td data-label="Apellido">{c.apellido}</td>
                  <td data-label="Nombre">{c.nombre}</td>
                  <td data-label="Documento">{c.tipo_documento} {c.numero_documento}</td>
                  <td data-label="Clases">
                    <div className="clases-badges">
                      {(c.clases || []).map((clase) => (
                        <span key={clase} className="badge-clase" title={tituloClase(catalogoClases, clase)}>{clase}</span>
                      ))}
                    </div>
                  </td>
                  <td data-label="Vencimiento">{formatearFecha(c.fecha_vencimiento)}</td>
                  <td data-label="Estado de licencia">
                    {c.estado_licencia && (
                      <span className={`badge-licencia badge-licencia-${ESTADOS_LICENCIA[c.estado_licencia].clase}`}>
                        {ESTADOS_LICENCIA[c.estado_licencia].label}
                        {sufijoEstadoImpresion(c) && ` · ${sufijoEstadoImpresion(c)}`}
                      </span>
                    )}
                  </td>
                  <td data-label="Estado">
                    <span className={`badge-estado badge-estado-${c.estado}`}>{c.estado}</span>
                  </td>
                  <td data-label="Acciones">
                    <div className="padron-acciones">
                      {c.estado_licencia !== 'vencida_fuera_de_gracia' && (
                        <button className="btn-accion btn-editar" onClick={() => abrirEditar(c.id)}>
                          <IconEditar {...ICONO_BOTON} /> Editar
                        </button>
                      )}
                      <button className="btn-accion btn-vista-previa" onClick={() => abrirVistaPrevia(c.id)}>
                        <IconVistaPrevia {...ICONO_BOTON} /> Vista previa
                      </button>
                      {/* Imprimir/Renovar/Reimprimir/Reactivar/Solicitar alta son
                          mutuamente excluyentes por fila:
                          - solicitud pendiente -> "Esperando autorización" (sin cambios)
                          - todavía no completó un ciclo de impresión (null/lista/rechazada) -> "Imprimir"
                            (o "Renovar"/"Reimprimir"/"Reactivar" si ya imprimió antes, según origen_reintento)
                          - completó un ciclo (impresa) -> depende de estado_licencia:
                            próxima a vencer / en gracia -> "Renovar"; vigente -> "Reimprimir";
                            vencida fuera de gracia -> "Solicitar alta" (con autorización de admin) */}
                      {c.estado_impresion !== 'impresa' ? (
                        c.tiene_solicitud_pendiente ? (
                          <button className="btn-accion btn-imprimir" disabled title="Un admin tiene que autorizar el reintento antes de poder imprimir de nuevo">
                            <IconImprimir {...ICONO_BOTON} /> Esperando autorización
                          </button>
                        ) : !c.fecha_impresion ? (
                          <button
                            className="btn-accion btn-imprimir"
                            onClick={() => imprimir(c)}
                            disabled={imprimiendoId !== null}
                          >
                            <IconImprimir {...ICONO_BOTON} /> {imprimiendoId === c.id ? 'Generando...' : 'Imprimir'}
                          </button>
                        ) : (
                          // Ya imprimió alguna vez (fecha_impresion no null) pero el
                          // ciclo actual no está 'impresa' (reintento tras rechazo,
                          // renovación sin terminar de imprimir, o reimpresión ya
                          // autorizada) -> reintentar directo con imprimir(c), sin
                          // pasar por pedirRenovar/confirmarRenovacion (esas ya
                          // corrieron antes; acá solo falta el paso de impresión).
                          // El texto, ícono y color distinguen el origen: 'reimpresion'
                          // -> "Reimprimir", 'reactivacion' -> "Reactivar" (mismo
                          // ícono/estilo que Reimprimir, también termina en imprimir);
                          // 'renovacion' (o null, dato viejo) -> "Renovar".
                          <button
                            className={`btn-accion ${
                              c.origen_reintento === 'reimpresion' || c.origen_reintento === 'reactivacion'
                                ? 'btn-imprimir'
                                : 'btn-renovar'
                            }`}
                            onClick={() => imprimir(c)}
                            disabled={imprimiendoId !== null}
                          >
                            {c.origen_reintento === 'reimpresion' || c.origen_reintento === 'reactivacion' ? (
                              <IconImprimir {...ICONO_BOTON} />
                            ) : (
                              <IconRenovar {...ICONO_BOTON} />
                            )}{' '}
                            {imprimiendoId === c.id
                              ? 'Generando...'
                              : c.origen_reintento === 'reimpresion'
                                ? 'Reimprimir'
                                : c.origen_reintento === 'reactivacion'
                                  ? 'Reactivar'
                                  : 'Renovar'}
                          </button>
                        )
                      ) : (
                        <>
                          {(c.estado_licencia === 'proxima_a_vencer' || c.estado_licencia === 'vencida_en_gracia') && (
                            c.tiene_solicitud_pendiente ? (
                              <button className="btn-accion btn-imprimir" disabled title="Un admin tiene que autorizar la solicitud pendiente antes de poder renovar">
                                <IconImprimir {...ICONO_BOTON} /> Esperando autorización
                              </button>
                            ) : (
                              <button
                                className="btn-accion btn-renovar"
                                onClick={() => pedirRenovar(c)}
                                disabled={renovandoId === c.id}
                              >
                                <IconRenovar {...ICONO_BOTON} /> {renovandoId === c.id ? 'Renovando...' : 'Renovar'}
                              </button>
                            )
                          )}
                          {c.estado_licencia === 'vigente' && (
                            c.tiene_solicitud_pendiente ? (
                              <button className="btn-accion btn-imprimir" disabled title="Un admin tiene que autorizar la reimpresión">
                                <IconImprimir {...ICONO_BOTON} /> Esperando autorización
                              </button>
                            ) : (
                              <button
                                className="btn-accion btn-renovar"
                                onClick={() => pedirReimpresion(c)}
                              >
                                <IconImprimir {...ICONO_BOTON} /> Reimprimir
                              </button>
                            )
                          )}
                          {c.estado_licencia === 'vencida_fuera_de_gracia' && (
                            c.tiene_solicitud_pendiente ? (
                              <button className="btn-accion btn-imprimir" disabled title="Un admin tiene que autorizar la reactivación">
                                <IconImprimir {...ICONO_BOTON} /> Esperando autorización
                              </button>
                            ) : esAdmin ? (
                              // Atajo para que el admin resuelva la reactivación él mismo,
                              // sin pasar por el pedido de un operador (ver reactivar-directo
                              // en CLAUDE.md). El operador sigue viendo "Solicitar alta".
                              <button
                                className="btn-accion btn-renovar"
                                onClick={() => abrirReactivacionAdmin(c)}
                              >
                                <IconAccionRapida {...ICONO_BOTON} /> Revisar y dar de alta
                              </button>
                            ) : (
                              <button
                                className="btn-accion btn-renovar"
                                onClick={() => abrirSolicitudAlta(c.id)}
                              >
                                <IconAccionRapida {...ICONO_BOTON} /> Solicitar alta
                              </button>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAbierto && (
        <PadronFormModal
          contribuyenteId={contribuyenteEditandoId}
          onClose={cerrarModal}
          onSaved={modoAdminDirectoActivo ? alGuardarReactivacionAdmin : alGuardar}
          modoSolicitud={modoSolicitudActivo}
          modoAdminDirecto={modoAdminDirectoActivo}
        />
      )}

      {previewId && (
        <PadronPreviewModal contribuyenteId={previewId} onClose={cerrarVistaPrevia} />
      )}

      {modalConfigFotoAbierto && (
        <ConfiguracionFotoModal
          onClose={() => setModalConfigFotoAbierto(false)}
          onGuardado={() => setModalConfigFotoAbierto(false)}
        />
      )}

      {confirmandoImpresion && (
        <ConfirmarImpresionModal
          nombreCompleto={confirmandoImpresion.nombreCompleto}
          procesando={procesandoAccionImpresion}
          onSi={confirmarImpresionSi}
          onNo={confirmarImpresionNo}
        />
      )}

      {reportandoFallo && (
        <ReportarFalloImpresionModal
          procesando={procesandoAccionImpresion}
          onConfirmar={enviarReporteFallo}
          onCancelar={cancelarReporteFallo}
        />
      )}

      {avisoCorteImpresion && (
        <ConfirmarCorteClasesModal
          nombreCompleto={`${avisoCorteImpresion.apellido}, ${avisoCorteImpresion.nombre}`}
          onCancelar={() => setAvisoCorteImpresion(null)}
          onConfirmar={() => {
            const c = avisoCorteImpresion;
            setAvisoCorteImpresion(null);
            imprimir(c, { corteConfirmado: true });
          }}
        />
      )}

      {imprimiendoDirecto && createPortal(
        <ZonaImprimible
          ref={zonaImprimibleRef}
          contribuyente={imprimiendoDirecto.contribuyente}
          diseno={imprimiendoDirecto.diseno}
        />,
        document.body
      )}

      {confirmandoRenovacion && (
        <ConfirmarRenovacionModal
          nombreCompleto={`${confirmandoRenovacion.apellido}, ${confirmandoRenovacion.nombre}`}
          procesando={renovandoId === confirmandoRenovacion.id}
          onConfirmar={confirmarRenovacion}
          onCancelar={cancelarRenovacion}
        />
      )}

      {solicitandoReimpresion && (
        <SolicitarReimpresionModal
          procesando={procesandoSolicitudReimpresion}
          onConfirmar={confirmarSolicitudReimpresion}
          onCancelar={cancelarSolicitudReimpresion}
        />
      )}
    </section>
  );
};

export default Padron;
